import React, { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import TableBuilder, { ApiRequestBuilder, ApiRequestBuilderV2, ButtonBuilder, ModalButtonBuilder, ModalLinkBuilder, TilesBuilder, ToastBuilder, RegisteredInputsBuilder } from '../../GlobalComponents/Builders';
import AsyncActionBanner from '../../GlobalComponents/Builders/AsyncActionBanner';
import { Container, Row, Card, CardBody } from 'reactstrap'
import { capitalizeFirstLetter, sortArrayByKeys, formatCWMNotes } from '../../GlobalComponents/Helpers';
import { convertArrayToCSV } from '../../GlobalComponents/Helpers';
import CountUp from 'react-countup';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { Form } from 'react-bootstrap';

const ProjectList = () => {

    const [isProcessing, setIsProcessing] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncActionId, setSyncActionId] = useState(null);
    const [projectData, setProjectData] = useState([]);
    const [allProjects, setAllProjects] = useState([]);
    const [availableStatuses, setAvailableStatuses] = useState([]);
    const allProjectsRef = useRef([]);
    const form = useForm();
    const { handleSubmit } = form;

    const [monthlyTimeRemaining, setMonthlyTimeRemaining] = useState(10);
    const [monthlyRecurringCost, setMonthlyRecurringCost] = useState(0);
    const [monthlyRecurringItems, setMonthlyRecurringItems] = useState([]);
    const [usedHoursDescriptions, setUsedHoursDescriptions] = useState([]);

    const tiles = [
        {
            id: 1,
            icon: 'Clock',
            title: 'Hours Remaining',
            footNote: `Ten Hour Monthly Agreement`,
            content: <ModalLinkBuilder
                title='Hour Usage'
                size='xl'
                customLabel={<CountUp className='tile-counter' delay={2} start={10} end={monthlyTimeRemaining} duration={7} useEasing={true} />}
                onSubmit={false}
                onCancelLabel='Close'
                body={
                    <Card>
                        <CardBody>
                            <TableBuilder
                                data={usedHoursDescriptions}
                                title=''
                                pagination={false}
                                processing={false}
                                columns={[
                                    {
                                        name: 'Dev',
                                        selector: (row) => capitalizeFirstLetter(row.enteredBy),
                                        sortable: true,
                                        center: true,
                                        maxWidth: '8%'
                                    },
                                    {
                                        name: 'Hours',
                                        selector: (row) => row.actualHours,
                                        sortable: true,
                                        center: true,
                                        maxWidth: '5%'
                                    },
                                    {
                                        name: 'Notes',
                                        selector: (row) => row.notes
                                            ? <div dangerouslySetInnerHTML={{ __html: formatCWMNotes(row.notes) }} />
                                            : '',
                                        sortable: false,
                                        center: false,
                                    }
                                ]}
                            />
                        </CardBody>
                    </Card>

                }
            />
        },
        {
            id: 2,
            icon: 'Activity',
            title: 'Active Projects',
            count: <CountUp start={0} end={allProjects.filter(data => !data.completed_date).length} duration={6} useEasing={true} />,
        },
        {
            id: 3,
            icon: 'Monitor',
            title: 'Viewing',
            count: <CountUp start={0} end={projectData.length} duration={2} useEasing={true} />,
        },
        {
            id: 4,
            icon: 'ShoppingBag',
            title: 'Recurring Cost',
            content: <ModalLinkBuilder
                title='Recurring Charges'
                size='xl'
                customLabel={<CountUp className='tile-counter' prefix={monthlyRecurringCost > 0 ? '$' : ''} delay={2} start={0} end={monthlyRecurringCost} duration={7} useEasing={true} />}
                onSubmit={false}
                onCancelLabel='Close'
                body={
                    <Container>
                    {/* <Card><CardBody> */}
                        <TableBuilder
                            subHeader={true}
                            columnsToSearch={['client', 'name', 'status']}
                            data={sortArrayByKeys([...monthlyRecurringItems], [{ effectiveDate: 'DESC' }])}
                            sortField={null}
                            processing={false}
                            pageSize={15}
                            columns={[
                                {
                                    name: 'Client',
                                    selector: (row) => row.invoiceDescription.split('/').slice(0, 1),
                                    sortable: true,
                                    center: true,
                                    width: '30%',

                                },
                                {
                                    name: 'Project',
                                    selector: (row) => row.invoiceDescription.split('/').slice(1),
                                    sortable: true,
                                    center: true,
                                },
                                {
                                    name: 'Amount',
                                    selector: (row) => '$' + String(row.unitPrice * row.quantity),
                                    sortable: true,
                                    center: true,
                                    width: '11%',
                                },
                                {
                                    name: 'Billing Start',
                                    selector: (row) => moment(row.effectiveDate).format('MM-DD-YYYY'),
                                    sortable: true,
                                    sortFunction: (a, b) => new Date(a.effectiveDate) - new Date(b.effectiveDate),
                                    center: true,
                                    width: '16%',
                                }
                            ]}
                        />
                    {/* </CardBody></Card> */}
                    </Container>
                }
            />,
        },
        {
            id: 5,
            icon: 'Layers',
            title: 'Number of Projects',
            count: <CountUp start={0} end={allProjects.length} duration={6} useEasing={true} />,
        },
    ]

    const handleCSVDownload = (data, fileName) => {
        if (data && data.length > 0) {
            const element = document.createElement("a");
            const file = new Blob([data], { type: 'text/csv' });
            element.href = URL.createObjectURL(file);
            element.download = fileName + ".csv";
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
        }
        else {
            ToastBuilder('error', 'No data available to export')
        }
    }

    const formatTicketNotes = (data) => {

        data = data ? data : projectData

        var projectWithNotes = []

        data.forEach(project => {

            var cleanProject = project
            var lastEntry = project.time_entries.length > 0 ? project.time_entries[project.time_entries.length - 1] : {};

            cleanProject.last_entry_date = lastEntry.date_entered ? lastEntry.date_entered : '-'
            cleanProject.last_entry_by = lastEntry.entered_by ? lastEntry.entered_by : '-'

            cleanProject.last_entry_notes = lastEntry.notes ? JSON.stringify(lastEntry.notes).replaceAll('/n', '').replaceAll('/n', '').replaceAll('/r', '') : ''

            delete cleanProject.project_id
            delete cleanProject.time_entries
            delete cleanProject.nn_partner_id
            delete cleanProject.recurring_price
            delete cleanProject.recurring_bill_active

            projectWithNotes.push(cleanProject)
        })

        return projectWithNotes
    }

    const buildTimeEntryExport = (projects) => {

        var all_time_entries = []

        projects.forEach(project => {
            let entries = project.time_entries
            if (typeof entries === 'string') {
                try { entries = JSON.parse(entries) } catch(e) { entries = [] }
            }
            if (entries && Array.isArray(entries) && entries.length > 0) {
                entries.forEach(entry => {
                    all_time_entries.push({
                        Post: 'Yes',
                        Date: entry.date_entered || '',
                        'Name of': 'Vendor',
                        'Employee/Vendor': 'Kraft & Kennedy',
                        Hours: entry.time_spent || 0,
                        Minutes: 0,
                        Billable: true,
                        'Billing Rate': '275.00',
                        'Cost Rate': '200.00',
                        'Customer/Project': project.partner_name || '',
                        Location: '',
                        Class: '',
                        Description: (entry.entered_by || '') + ' - ' + (project.name || '') + ' - ' + (entry.notes ? JSON.stringify(entry.notes).replaceAll(',', '').replaceAll('/n', '').replaceAll('/r', '') : '')
                    })
                })
            }
        })

        return all_time_entries
    }

    const handleStatusFilter = (statusSelection) => {
        setProjectData(allProjects.filter(data => data.status === statusSelection.status))
    }

    const reloadProjectData = useCallback(() => {
        return ApiRequestBuilder('projectsGet').then(results => {
            if (results.data) {
                allProjectsRef.current = results.data;
                setAllProjects(results.data);
                setProjectData(results.data);
                setAvailableStatuses([...new Set(results.data.map(obj => obj['status']))]);
            }
            setIsProcessing(false);
        });
    }, []);

    const refreshProjects = () => {
        if (isSyncing) {
            console.log('[refreshProjects] Already syncing — skipping');
            return Promise.resolve();
        }

        setIsSyncing(true);
        console.log('[refreshProjects] Firing developmentProjectsCachePost');
        return ApiRequestBuilderV2('developmentProjectsCachePost').then(res => {
            console.log('[refreshProjects] POST response:', res);
            if (res.success && res.data?.actionId) {
                console.log('[refreshProjects] Got actionId:', res.data.actionId);
                setSyncActionId(res.data.actionId);
            } else {
                console.log('[refreshProjects] No actionId — falling back to direct fetch');
                setIsSyncing(false);
                reloadProjectData();
            }
        });
    }

    const handleSyncComplete = useCallback(() => {
        console.log('[async-poll] Sync completed!');
        reloadProjectData();
        setIsSyncing(false);
        setSyncActionId(null);
    }, [reloadProjectData]);

    const handleSyncFailed = useCallback((action) => {
        console.log('[async-poll] Sync failed:', action?.message);
        setIsSyncing(false);
        setSyncActionId(null);
    }, []);

    useEffect(() => {

        document.title = 'View Projects'

        // Load existing DB data immediately so the page is usable
        ApiRequestBuilder('projectsGet')
            .then(function (results) {
                if (results.data) {
                    allProjectsRef.current = results.data
                    setAllProjects(results.data)
                    setProjectData(results.data)
                    setAvailableStatuses([...new Set(results.data.map(obj => obj['status']))])
                }
                setIsProcessing(false)
            })

        // Kick off async cache sync in the background (polls for completion)
        refreshProjects().catch(() => { })

        ApiRequestBuilder('developmentProjectsGet')
            .then(function (results) {

                if (results.data) {
                    setUsedHoursDescriptions(results.data.usedHoursDescriptions)
                    setMonthlyRecurringCost(results.data.monthlyRecurringCost)
                    setMonthlyRecurringItems(results.data.monthlyRecurringItems)
                    setMonthlyTimeRemaining(results.data.remainingHours)
                }
            }
            )

    }, []);

    return (
        <Fragment>
            <TilesBuilder data={tiles} fluid />
            <Row>
                <Container>
                    <AsyncActionBanner
                        actionId={syncActionId}
                        message='Syncing projects from CWM — this may take a moment...'
                        successMessage='Project cache synced — data refreshed!'
                        onComplete={handleSyncComplete}
                        onFailed={handleSyncFailed}
                        showRefresh={true}
                        onRefresh={reloadProjectData}
                        autoDismiss={8000}
                    />
                    <Card>
                        <CardBody>
                            <TableBuilder
                                subHeader={true}
                                columnsToSearch={['partner_name', 'name', 'status']}
                                data={sortArrayByKeys([...projectData], [{ last_updated: 'DESC' }, 'partner_name'])}
                                title='Projects'
                                sortField={null}
                                // sortOrderAsc = {false}
                                processing={isProcessing}
                                pagination={false}
                                paginationPerPage={30}
                                styles={{
                                    cells: {
                                        style: {
                                            paddingTop: 10,
                                            paddingBottom: 10,
                                        }
                                    }
                                }}
                                actions={
                                    <Fragment>
                                        <ButtonBuilder label={'All'} className='btn-primary m-1' useSpinner={false} onClick={() => setProjectData(allProjects)} />
                                        <ButtonBuilder label={'Open'} className='btn-primary m-1' useSpinner={false} onClick={() => setProjectData(allProjects.filter(data => !data.completed_date))} />
                                        <ButtonBuilder label={'Recent'} className='btn-primary m-1' useSpinner={false} onClick={() => setProjectData(allProjects.filter(data => !data.completed_date || moment(data.completed_date).isAfter(moment().subtract(120, 'days'))))} />
                                        <ModalButtonBuilder title='Filter Status' className='btn-secondary m-1' color={null}
                                            body={
                                                <Form id='filter-status-form' onSubmit={handleSubmit(handleStatusFilter)}>
                                                    <RegisteredInputsBuilder
                                                        form={form}
                                                        inputs={[
                                                            {
                                                                label: 'Status',
                                                                type: 'reactselect',
                                                                size: 10,
                                                                required: false,
                                                                options: availableStatuses.map(s => ({ label: s, value: s })),
                                                                name: 'status'
                                                            }
                                                        ]} />
                                                </Form>
                                            }
                                        />
                                        <ButtonBuilder label={'Refresh'} className='btn-danger m-1' disabled={isSyncing} onClick={() => {
                                            setIsProcessing(true)
                                            refreshProjects()
                                                .catch(() => setIsProcessing(false))
                                        }} />
                                        <ButtonBuilder label={'Project Export'} className='btn-secondary m-1' onClick={() => handleCSVDownload(convertArrayToCSV(formatTicketNotes(projectData)), 'kraftkennedy_time_log')} />
                                        <ButtonBuilder label={'Time Export'} className='btn-primary m-1' onClick={() => {
                                            const entries = buildTimeEntryExport(projectData)
                                            if (!entries.length) return ToastBuilder('error', 'No time entries to export')
                                            handleCSVDownload(convertArrayToCSV(entries), 'kraftkennedy_billing_log')
                                        }} />
                                    </Fragment>
                                }
                                columns={[
                                    {
                                        name: 'Partner',
                                        selector: (row) => row.partner_name || 'Unknown',
                                        sortable: true,
                                        center: true,
                                        width: '15%',
                                    },
                                    {
                                        name: 'Project',
                                        selector: (row) => <span title={row.name}>{row.name}</span>,
                                        sortable: true,
                                        center: true,
                                    },
                                    {
                                        name: 'Time Spent',
                                        selector: (row) => row.time_spent,
                                        sortable: true,
                                        center: true,
                                        width: '10%'
                                    },
                                    {
                                        name: 'Time Alloted',
                                        selector: (row) => row.time_alloted,
                                        sortable: true,
                                        center: true,
                                        width: '10%'
                                    },
                                    {
                                        name: 'Status',
                                        selector: (row) => row.status,
                                        sortable: true,
                                        center: true,
                                        width: '13%'
                                    },
                                    {
                                        name: 'Updated',
                                        selector: (row) => row.last_updated ? moment(row.last_updated).format('MM-DD-YYYY') : '-',
                                        sortable: true,
                                        center: true,
                                        width: '10%'
                                    },
                                    {
                                        name: 'Closed', // When closed & whens been touched
                                        selector: (row) => row.completed_date ? moment(row.completed_date).format('MM-DD-YYYY') : '-',
                                        sortable: true,
                                        center: true,
                                        width: '10%'
                                    },
                                    {
                                        name: 'Notes',
                                        selector: (row) => row.time_entries && row.time_entries.length === 0 ? '-' : <ModalLinkBuilder
                                            title={`${row.name} - Time Entries & Notes`}
                                            style={{ maxWidth: '75%' }}
                                            size='xl'
                                            customLabel='View'
                                            onSubmit={false}
                                            onCancelLabel='Close'
                                            body={
                                                <Card>
                                                    <CardBody>
                                                        <TableBuilder
                                                            data={row.time_entries}
                                                            title=''
                                                            pagination={false}
                                                            processing={false}
                                                            sortField={1}
                                                            sortOrderAsc={false}
                                                            styles={{
                                                                cells: {
                                                                    style: {
                                                                        paddingTop: '3px',
                                                                        paddingBottom: '3px',
                                                                    }
                                                                }
                                                            }}
                                                            columns={[
                                                                {
                                                                    name: 'Date',
                                                                    selector: (row) => moment(row.date_entered).format('MM-DD-YYYY'),
                                                                    sortable: true,
                                                                    sortFunction: (a, b) => new Date(a.date_entered) - new Date(b.date_entered),
                                                                    center: true,
                                                                    width: '135px',
                                                                },
                                                                {
                                                                    name: 'Developer',
                                                                    selector: (row) => row.entered_by,
                                                                    sortable: true,
                                                                    center: false,
                                                                    width: '180px',
                                                                },
                                                                {
                                                                    name: 'Hours',
                                                                    selector: (row) => row.time_spent,
                                                                    sortable: true,
                                                                    center: true,
                                                                    width: '100px'
                                                                },
                                                                {
                                                                    name: 'Notes',
                                                                    selector: (row) => row.notes
                                                                        ? <div dangerouslySetInnerHTML={{ __html: formatCWMNotes(row.notes) }} />
                                                                        : '',
                                                                    sortable: false,
                                                                    center: false,
                                                                    wrap: true
                                                                }
                                                            ]}
                                                        />
                                                    </CardBody>
                                                </Card>

                                            }
                                        />,
                                        sortable: true,
                                        center: true,
                                        width: '7%'
                                    }
                                ]}
                            />
                        </CardBody>
                    </Card>
                </Container>
            </Row>
        </Fragment>
    );
};

export default ProjectList;